import React from 'react';
import {
  ButtonElement,
  ToggleElement,
  SearchElement,
  DropdownElement,
  ModalTriggerElement,
  TextElement,
} from '../elements';
import type { TopBarElement } from '@frontend/Models/TopBar';

/**
 * Context for rendering elements
 * Contains shared data and callbacks needed by elements
 */
export interface ElementRenderContext {
  openModal?: (modalId: string) => void;
  pageContext?: Record<string, any>;
}

/**
 * Renders the appropriate element component based on element type
 *
 * @param element - Element configuration object
 * @param index - Index in the elements array (used for key)
 * @param context - Shared context (modal callbacks, page data, etc.)
 * @returns Rendered element component or null
 */
export const renderElement = (
  element: TopBarElement,
  index: number,
  context: ElementRenderContext = {}
): React.ReactElement | null => {
  const { openModal } = context;

  switch (element.type) {
    case 'button':
      return <ButtonElement key={index} {...element} />;

    case 'toggle':
      return <ToggleElement key={index} {...element} />;

    case 'search':
      return <SearchElement key={index} {...element} />;

    case 'dropdown':
      return <DropdownElement key={index} {...element} />;

    case 'modalTrigger':
      return (
        <ModalTriggerElement key={index} {...element} openModal={openModal} />
      );

    case 'text':
      return <TextElement key={index} {...element} />;

    default:
      // Log warning for unknown element types
      console.warn(`Unknown element type: ${(element as any).type}`);
      return null;
  }
};

/**
 * Renders an array of elements
 *
 * @param elements - Array of element configurations or JSX elements
 * @param context - Shared context for all elements
 * @returns Array of rendered elements (filtered for nulls)
 */
export const renderElements = (
  elements: (TopBarElement | React.ReactElement)[] = [],
  context: ElementRenderContext = {}
): React.ReactElement[] => {
  return elements
    .map((element, index) => {
      // Check if it's a React element (JSX)
      if (React.isValidElement(element)) {
        return React.cloneElement(element, { key: index });
      }
      // Otherwise treat as TopBarElement configuration
      return renderElement(element as TopBarElement, index, context);
    })
    .filter((el): el is React.ReactElement => el !== null);
};
